import * as Sentry from '@sentry/sveltekit';
import type { LocalsMarketType, LocalsUserType } from '$lib/types/index';

/**
 * Set user context in Sentry for better error tracking
 */
export function setSentryUser(user: LocalsUserType | null) {
	if (!user) {
		Sentry.setUser(null);
		return;
	}

	Sentry.setUser({
		id: user.id.toString(),
		email: user.email,
		username: user.username,
		segment: getUserSegment(user),
	});

	// Add additional context
	Sentry.setContext('user_details', {
		isAdmin: user.isAdmin,
		isSuperuser: user.isSuperuser,
		isGrower: user.isGrower,
		isVolunteer: user.isVolunteer,
		marketId: user.marketId,
		growerId: user.growerId,
		active: user.active,
	});
}

/**
 * Set market context in Sentry
 */
export function setSentryMarket(market: LocalsMarketType | null) {
	if (!market) {
		Sentry.setContext('market', null);
		return;
	}

	Sentry.setContext('market', {
		id: market.id,
		name: market.name,
		subdomain: market.subdomain,
		integratedPayments: market.integratedPayments,
		stripeApproved: market.stripeApproved,
		requiresMembership: market.requiresMembership,
	});

	// Set tags for easier filtering
	Sentry.setTag('market.id', market.id);
	Sentry.setTag('market.subdomain', market.subdomain);
}

/**
 * Add breadcrumb for important user actions
 */
export function addActionBreadcrumb(
	message: string,
	data?: Record<string, unknown>,
	level: Sentry.SeverityLevel = 'info'
) {
	Sentry.addBreadcrumb({
		message,
		level,
		category: 'user-action',
		data,
		timestamp: Date.now() / 1000,
	});
}

/**
 * Track business transactions with custom spans
 */
export async function trackTransaction<T>(
	name: string,
	operation: string,
	callback: () => Promise<T>,
	data?: Record<string, unknown>
): Promise<T> {
	return await Sentry.startSpan(
		{
			name,
			op: operation,
			attributes: data as Record<string, string | number | boolean | undefined>,
		},
		async () => {
			try {
				const result = await callback();
				Sentry.setMeasurement(`${operation}.success`, 1, 'none');
				return result;
			} catch (error) {
				Sentry.setMeasurement(`${operation}.failure`, 1, 'none');
				throw error;
			}
		}
	);
}

/**
 * Track performance of database queries
 */
export async function trackDatabaseQuery<T>(
	queryName: string,
	callback: () => Promise<T>
): Promise<T> {
	return await Sentry.startSpan(
		{
			name: `db.query.${queryName}`,
			op: 'db.query',
			attributes: {
				'db.system': 'mysql',
				'db.name': 'locallygrown',
			},
		},
		callback
	);
}

/**
 * Track API calls with performance monitoring
 */
export async function trackApiCall<T>(
	endpoint: string,
	method: string,
	callback: () => Promise<T>
): Promise<T> {
	return await Sentry.startSpan(
		{
			name: `${method} ${endpoint}`,
			op: 'http.client',
			attributes: {
				'http.method': method,
				'http.url': endpoint,
			},
		},
		callback
	);
}

/**
 * Enhanced error capture with business context
 */
export function captureBusinessError(
	error: Error,
	context: {
		operation?: string;
		marketId?: number;
		userId?: number;
		orderId?: number;
		productId?: number;
		[key: string]: unknown;
	}
) {
	Sentry.withScope((scope) => {
		scope.setContext('business_operation', context);
		if (context.operation) {
			scope.setTag('business.operation', context.operation);
		}
		scope.setLevel('error');
		Sentry.captureException(error);
	});
}

/**
 * Track checkout process with detailed monitoring
 */
export function trackCheckoutStep(
	step: 'start' | 'shipping' | 'payment' | 'confirm' | 'complete' | 'abandon',
	data?: {
		cartTotal?: number;
		itemCount?: number;
		paymentMethod?: string;
		error?: string;
	}
) {
	addActionBreadcrumb(`Checkout: ${step}`, data, 'info');

	// Track as custom event for analytics
	Sentry.captureEvent({
		message: `Checkout ${step}`,
		level: 'info',
		tags: {
			'checkout.step': step,
		},
		extra: data,
	});
}

/**
 * Monitor cache performance
 */
export function trackCacheOperation(
	operation: 'hit' | 'miss' | 'set' | 'delete',
	cacheKey: string,
	duration?: number
) {
	Sentry.addBreadcrumb({
		message: `Cache ${operation}: ${cacheKey}`,
		category: 'cache',
		level: 'debug',
		data: {
			operation,
			cacheKey,
			duration,
		},
	});

	// Track cache metrics
	Sentry.setMeasurement(`cache.${operation}`, 1, 'none');
	if (duration) {
		Sentry.setMeasurement(`cache.${operation}.duration`, duration, 'millisecond');
	}
}

/**
 * Get user segment for better error grouping
 */
function getUserSegment(user: LocalsUserType): string {
	if (user.isSuperuser) return 'superuser';
	if (user.isAdmin) return 'admin';
	if (user.isGrower) return 'grower';
	if (user.isVolunteer) return 'volunteer';
	return 'customer';
}

/**
 * Filter sensitive data before sending to Sentry
 */
export function beforeSendFilter(
	event: Sentry.ErrorEvent,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
	// Filter out sensitive data patterns
	const sensitivePatterns = [
		/password/i,
		/secret/i,
		/token/i,
		/api[_-]?key/i,
		/stripe/i,
		/credit[_-]?card/i,
		/ssn/i,
		/social[_-]?security/i,
	];

	// Don't send events from bots
	const botPatterns = [/bot/i, /crawler/i, /spider/i, /scraper/i];
	const userAgent = event.request?.headers?.['user-agent'] || '';
	if (botPatterns.some((pattern) => pattern.test(userAgent))) {
		return null;
	}

	// Recursively filter object
	function filterSensitive(obj: unknown): unknown {
		if (typeof obj !== 'object' || obj === null) {
			return obj;
		}

		if (Array.isArray(obj)) {
			return obj.map(filterSensitive);
		}

		const filtered: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(obj)) {
			if (sensitivePatterns.some((pattern) => pattern.test(key))) {
				filtered[key] = '[FILTERED]';
			} else if (typeof value === 'string' && value.length > 0) {
				// Check if value looks like sensitive data
				if (
					value.startsWith('sk_') || // Stripe secret key
					value.startsWith('pk_') || // Stripe public key
					value.startsWith('whsec_') || // Stripe webhook secret
					value.length === 64 // Possible API key or hash
				) {
					filtered[key] = '[FILTERED]';
				} else {
					filtered[key] = value;
				}
			} else {
				filtered[key] = filterSensitive(value);
			}
		}
		return filtered;
	}

	// Filter the event
	if (event.extra) {
		event.extra = filterSensitive(event.extra) as Record<string, unknown>;
	}
	if (event.contexts) {
		event.contexts = filterSensitive(event.contexts) as typeof event.contexts;
	}
	if (event.request?.data) {
		event.request.data = filterSensitive(event.request.data);
	}

	return event;
}

/**
 * Get environment-specific Sentry configuration
 */
export function getSentryConfig(environment: string = 'development') {
	const isProduction = environment === 'production';
	const isDevelopment = environment === 'development';

	return {
		// Performance monitoring
		tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in production, 100% in dev
		profilesSampleRate: isProduction ? 0.1 : 0, // Profile 10% of production transactions

		// Session replay
		replaysSessionSampleRate: isProduction ? 0.01 : 0.1, // 1% in production, 10% in dev
		replaysOnErrorSampleRate: 1.0, // Always replay on error

		// Network tracking
		tracePropagationTargets: [
			'localhost',
			/^https:\/\/.*\.locallygrown\.net/,
			/^https:\/\/.*\.lcl\.host/,
			/^\/api\//,
		],

		// Error filtering
		ignoreErrors: [
			// Browser errors
			'ResizeObserver loop limit exceeded',
			'ResizeObserver loop completed with undelivered notifications',
			'Non-Error promise rejection captured',
			// Network errors
			'NetworkError',
			'Failed to fetch',
			'Load failed',
			// User-caused errors
			'User cancelled',
			'User denied',
			// Bot errors
			'Illegal invocation',
		],

		// URL filtering
		denyUrls: [
			// Chrome extensions
			/extensions\//i,
			/^chrome:\/\//i,
			/^chrome-extension:\/\//i,
			// Firefox extensions
			/^moz-extension:\/\//i,
			// Safari extensions
			/^safari-extension:\/\//i,
			// Other browser internals
			/^about:/i,
			/^file:/i,
			// Common bot patterns
			/127\.0\.0\.1/,
			/localhost:\d+/,
		],

		// Development settings
		debug:
			isDevelopment &&
			(process.env.SENTRY_DEBUG === 'true' || process.env.PUBLIC_SENTRY_DEBUG === 'true'),
		enabled:
			isProduction ||
			process.env.SENTRY_ENABLED === 'true' ||
			process.env.PUBLIC_SENTRY_ENABLED === 'true',

		// Attachments
		maxValueLength: 1000,
		normalizeDepth: 5,

		// Transport options - removed as it causes type conflicts between Node and Browser
	};
}
